import { useState, useRef, useCallback } from 'react'
import { supabase, updateOrganization } from '@unvld/db'
import { toast } from 'sonner'
import { Upload } from 'lucide-react'

const MAX_SIZE = 2 * 1024 * 1024 // 2 MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']
const ALLOWED_EXT_LABEL = 'PNG, JPEG, WebP, SVG'

interface LogoUploadProps {
  orgId: string
  currentLogoUrl?: string | null
  onUploaded?: (url: string) => void
}

export function LogoUpload({ orgId, currentLogoUrl, onUploaded }: LogoUploadProps) {
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const displayUrl = preview || currentLogoUrl || null

  const validate = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Ungültiges Format. Erlaubt: ${ALLOWED_EXT_LABEL}`
    }
    if (file.size > MAX_SIZE) {
      return 'Datei ist zu groß (max. 2 MB)'
    }
    return null
  }

  const upload = useCallback(async (file: File) => {
    const error = validate(file)
    if (error) {
      toast.error(error)
      return
    }

    // Show preview immediately
    const objectUrl = URL.createObjectURL(file)
    setPreview(objectUrl)
    setUploading(true)

    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png'
      const path = `${orgId}/logo.${ext}`

      // Upload to Supabase Storage (upsert to overwrite previous logo)
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(path, file, { upsert: true, contentType: file.type })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(path)

      const publicUrl = urlData.publicUrl

      // Append cache-buster so browser shows new logo immediately
      const finalUrl = `${publicUrl}?v=${Date.now()}`

      // Update organization record
      await updateOrganization(orgId, { logo_url: finalUrl } as any)

      setPreview(finalUrl)
      onUploaded?.(finalUrl)
      toast.success('Logo hochgeladen')
    } catch (err: any) {
      // Revert preview on failure
      setPreview(null)
      toast.error(err.message || 'Upload fehlgeschlagen')
    } finally {
      setUploading(false)
    }
  }, [orgId, onUploaded])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) upload(file)
    // Reset input so same file can be re-selected
    e.target.value = ''
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) upload(file)
  }, [upload])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        className="hidden"
        onChange={handleFileChange}
      />

      {displayUrl ? (
        /* Has logo: show it with hover overlay */
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          disabled={uploading}
          className="relative group w-32 h-32 rounded-xl overflow-hidden border border-slate-700 hover:border-indigo-500 transition-colors cursor-pointer"
        >
          <img
            src={displayUrl}
            alt="Club Logo"
            className="w-full h-full object-cover"
          />
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            {uploading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="text-sm font-medium text-white">Ändern</span>
            )}
          </div>
          {/* Uploading spinner (always visible during upload) */}
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </button>
      ) : (
        /* No logo: upload zone */
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          disabled={uploading}
          className={`w-full border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            dragOver
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-slate-700 hover:border-indigo-500'
          }`}
        >
          {uploading ? (
            <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          ) : (
            <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
          )}
          <p className="text-sm text-slate-400">
            {uploading ? 'Wird hochgeladen...' : 'Logo hochladen'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {ALLOWED_EXT_LABEL}, max. 2 MB
          </p>
        </button>
      )}
    </div>
  )
}
